import React, { useEffect, useMemo } from 'react'
import { maxlength, minlength, required, link } from 'validation-rules'
import { makeStyles } from '@material-ui/core'
import { getValue } from 'remote-config-value'
import RedAsterisk from '../../RedAsterisk'
import LabelTextareaInfo from './LabelTextareInfo'
import useFlatForm from '../../../hooks/useFlatForm'
import FileUploader, { EncodedFile } from './FileUploader'
import { Link, Select, TextField, Typography, Checkbox } from 'foundations-library/components'
import { replacePlaceholderWithActualBrandValues } from '../../../utils/util'

const MESSAGE_FLOW_MIN_LENGTH = 40
const MESSAGE_FLOW_MAX_LENGTH = 4096

const jss = () => {
    // const { colors } = theme['foundations-library']

    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: 20
        },
        inputLabel: {
            marginBottom: 15
        },
        mb10: {
            marginBottom: 10
        },
        mb30: {
            marginBottom: 30
        },
        textarea: {
            marginBottom: 20,
            '& textarea': {
                minHeight: 150
            }
        },
        inputContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: 30
        }
    }
}

const useStyles = makeStyles(jss)

interface PropsInterface {
    disabled: boolean
    initialValue: {
        messageFlow: string
        consentInfo: {
            initialContact: string
            pointOfVerbalContact: string
            publiclyAccessibleUrl: string
            itIncludesOptInLanguage: boolean
            itIncludesLinkToPrivacyPolicy: boolean
        }
        files: EncodedFile[]
    },
    onChange(v): unknown
    isValid(obj: Record<string, boolean>): unknown
    item?: Record<string, unknown>,
    brand?: undefined | Record<string, unknown>,
    privacyPolicyLink?: string
}

const getMessageFlowValue = (userInput, cInfo) => {
    if (cInfo.initialContact === 'Website form') {
        const defaultValue = getValue('sms_enrollment_message_flow_value_if_website_form_selected')
        return defaultValue
            .replace('[PUBLICLY_ACCESSIBLE_URL]', cInfo?.publiclyAccessibleUrl)
    }

    return userInput
}

const replaceBrandAndPrivacyPolicyLink = (str, brand, privacyPolicyLink) => {
    return (
        replacePlaceholderWithActualBrandValues(
            str,
            brand
        )
    ).replaceAll('[PRIVACY_POLICY_LINK]', privacyPolicyLink || '[PRIVACY_POLICY_LINK]')
}

/**
 *
 */
const MessageFlow = ({
    onChange,
    isValid,
    disabled,
    initialValue,
    item,
    brand,
    privacyPolicyLink
}: PropsInterface): JSX.Element => {
    const styles = useStyles()
    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp
    } = useFlatForm({ ...initialValue })

    const {
        data: cInfoData,
        onClear: cInfoOnClear,
        setDataProp: cInfoSetDataProp
    } = useFlatForm({ ...initialValue.consentInfo })

    const websiteCheckboxes = [
        {
            label: getValue('sms_enrollment_campaigns_1st_step_website_form_it_includes_opt_in_language'),
            checked: cInfoData.itIncludesOptInLanguage,
            prop: 'itIncludesOptInLanguage'
        },
        {
            label: getValue('sms_enrollment_campaigns_1st_step_website_form_it_includes_link_to_privacy_policy'),
            checked: cInfoData.itIncludesLinkToPrivacyPolicy,
            prop: 'itIncludesLinkToPrivacyPolicy'
        }
    ]
    const verbalOptions = useMemo(() => {
        return {
            'phone-call': {
                text: 'Phone call',
                label: getValue('sms_enrollment_campaigns_1st_step_describe_how_obtein_consent_over_phone'),
                info: replaceBrandAndPrivacyPolicyLink(
                    getValue('sms_enrollment_campaigns_1st_step_verbal_phone_sample_description'),
                    brand,
                    privacyPolicyLink
                )
            },
            'web-meeting': {
                text: 'Web meeting',
                label: getValue('sms_enrollment_campaigns_1st_step_describe_how_obtein_consent_during_web_meeting'),
                info: replaceBrandAndPrivacyPolicyLink(
                    getValue('sms_enrollment_campaigns_1st_step_describe_web_meeting'),
                    brand,
                    privacyPolicyLink
                )
            },
            'in-person': {
                text: 'In-person conversation',
                label: getValue('sms_enrollment_campaigns_1st_step_describe_how_obtein_consent_in_person'),
                info: replaceBrandAndPrivacyPolicyLink(
                    getValue('sms_enrollment_campaigns_1st_step_describe_in_person'),
                    brand,
                    privacyPolicyLink
                )
            }
        }
    }, [brand, privacyPolicyLink])

    useEffect(() => {
        onChange({
            ...data,
            messageFlow: getMessageFlowValue(data.messageFlow, cInfoData),
            consentInfo: cInfoData
        })
        const consentInfoVaidation = {
            initialContact: !!cInfoData.initialContact,
            pointOfVerbalContact: cInfoData.initialContact === 'Verbally'
                ? !!cInfoData.pointOfVerbalContact
                : true,
            publiclyAccessibleUrl: cInfoData.initialContact === 'Website form'
                ? !!cInfoData.publiclyAccessibleUrl
                : true,
            itIncludesOptInLanguage: cInfoData.initialContact === 'Website form'
                ? Boolean(cInfoData.itIncludesOptInLanguage)
                : true,
            itIncludesLinkToPrivacyPolicy: cInfoData.initialContact === 'Website form'
                ? Boolean(cInfoData.itIncludesLinkToPrivacyPolicy)
                : true
        }

        const isMessageFlowValid = !!data.messageFlow && data.messageFlow?.length && data.messageFlow.length >= MESSAGE_FLOW_MIN_LENGTH && data.messageFlow.length <= MESSAGE_FLOW_MAX_LENGTH

        isValid({
            messageFlow: cInfoData.initialContact !== 'Website form' ? isMessageFlowValid : true,
            files: cInfoData.initialContact === 'Paper/electronic form' ? !!data?.files?.length : true,
            consentInfo: Object.values(consentInfoVaidation).every((x) => x)
        })
    }, [data, cInfoData])

    const obteinConsentOptions = [
        'Verbally',
        'Email',
        'Website form',
        'Paper/electronic form',
        'Other'
    ]

    const messageFlowProps = {
        rules: [required(), minlength(MESSAGE_FLOW_MIN_LENGTH), maxlength(MESSAGE_FLOW_MAX_LENGTH)],
        placeholder: 'Enter text here',
        isValid: setIsValidProp('messageFlow'),
        onClear: onClear('messageFlow'),
        value: data.messageFlow,
        onChange: setDataProp('messageFlow'),
        disabled: disabled,
        className: styles.textarea,
        'data-testid': 'essage-flow'
    }

    return <div className={styles.container} data-testid='flow'>
        <div>
            <Typography variant='body1' className={styles.inputLabel} data-testid='flow-infolabel'>
                {getValue('sms_enrollment_campaigns_1st_step_how_to_obtain_customer_consent')}<RedAsterisk />
            </Typography>
            <Select
                options={obteinConsentOptions.map((x) => ({ text: x, value: x }))}
                value={cInfoData.initialContact}
                onChange={cInfoSetDataProp('initialContact')}
                onClear={cInfoOnClear('initialContact')}
                rules={[required()]}
                disabled={disabled}
                data-testid='initial-contact'
            />
        </div>
        {
            cInfoData.initialContact === 'Verbally' &&
            <div>
                <div className={styles.mb30}>
                    <Typography variant='body1' className={styles.inputLabel} data-testid='flow-verbally'>
                        {getValue('sms_enrollment_campaigns_1st_step_whats_the_point_of_verbal_contact')}<RedAsterisk />
                    </Typography>
                    <Select
                        options={Object.entries(verbalOptions).map(([key, value]) => ({ text: value.text, value: key }))}
                        value={cInfoData.pointOfVerbalContact}
                        onChange={cInfoSetDataProp('pointOfVerbalContact')}
                        onClear={cInfoOnClear('pointOfVerbalContact')}
                        rules={[required()]}
                        disabled={disabled}
                        data-testid='flow-verbally-select3'
                    />
                </div>
                {cInfoData.pointOfVerbalContact &&
                    <LabelTextareaInfo
                        {...messageFlowProps}
                        label={verbalOptions[cInfoData.pointOfVerbalContact].label}
                        info={verbalOptions[cInfoData.pointOfVerbalContact].info}
                    />}
            </div>
        }
        {
            cInfoData.initialContact === 'Email' &&
            <LabelTextareaInfo
                {...messageFlowProps}
                label={getValue('sms_enrollment_campaigns_1st_step_whats_the_point_of_email_contanct')}
                info={getValue('sms_enrollment_campaigns_1st_step_email_sample')}
                placeholder='Enter text here'
            />
        }
        {
            cInfoData.initialContact === 'Website form' &&
            <div className={styles.inputContainer}>
                <div>
                    <Typography variant='body1' className={styles.inputLabel} data-testid='flow-website'>
                        {getValue('sms_enrollment_campaigns_1st_step_website_form_url')}<RedAsterisk />
                    </Typography>
                    <TextField
                        type='url'
                        placeholder='Enter URL...'
                        rules={[required(), link()]}
                        disabled={disabled}
                        value={cInfoData.publiclyAccessibleUrl}
                        onChange={cInfoSetDataProp('publiclyAccessibleUrl')}
                        onClear={cInfoOnClear('publiclyAccessibleUrl')}
                        data-testid='flow-website-textfield'
                    />
                </div>
                <div>
                    <Typography variant='body1' className={styles.inputLabel} data-testid='flow-cbs'>
                        {getValue('sms_enrollment_campaigns_1st_step_website_form_confirm_about')}<RedAsterisk />
                    </Typography>
                    {websiteCheckboxes.map((cb, index) => (
                        <Checkbox
                            {...cb}
                            rules={[required()]}
                            onChange={cInfoSetDataProp(cb.prop)}
                            checked={cInfoData[cb.prop]}
                            disabled={disabled}
                            key={cb.label}
                            data-testid={`flow-website-cb-${index}`}
                        />
                    ))}
                </div>
            </div>
        }
        {
            cInfoData.initialContact === 'Paper/electronic form' &&
            <div className={styles.inputContainer}>
                <div>
                    <FileUploader
                        value={data.files}
                        onChange={setDataProp('files')}
                        disabled={disabled}
                        item={item}
                    />
                </div>
                <LabelTextareaInfo
                    {...messageFlowProps}
                    label={getValue('sms_enrollment_campaigns_1st_step_paper_describe_how_to')}
                    info={getValue('sms_enrollment_campaigns_1st_step_paper_sample_description')}
                />
            </div>
        }
        {
            cInfoData.initialContact === 'Other' &&
            <div className={styles.inputContainer}>
                <LabelTextareaInfo
                    {...messageFlowProps}
                    label={<span>
                        Please review the <Link href={getValue('sms_registration_consent_guidelines_link')} target='_blank'>consent guidelines</Link> to ensure that you meet carrier requirements. Then describe in detail how you get customer consent before messaging them.
                    </span>}
                    info={getValue('sms_enrollment_campaigns_1st_step_paper_sample_description')}
                />
            </div>
        }
    </div >
}

export default MessageFlow
